/**
 * Consent template admin wire types (CG-F4, PROD-1067).
 *
 * 1:1 mirror of the Scrips.Patient `ConsentTemplateController` DTOs (PR #882,
 * authored in the same workstream — read from source, not inferred). Routes:
 *
 *   POST /api/ConsentTemplate/CreateUpdateTemplate  ConsentTemplateBody → string (GUID)
 *   GET  /api/ConsentTemplate/List                                       → ConsentTemplate[]
 *   GET  /api/ConsentTemplate/{id}                                       → ConsentTemplate
 *   POST /api/ConsentTemplate/Publish/{id}?actorId=                       → boolean
 *   POST /api/ConsentTemplate/Retire/{id}?actorId=                        → boolean
 *
 * `versionHash` is computed server-side (SHA-256 of the bilingual body) — the
 * client never sends it; it reads it back and ships it on CreateConsent so a
 * signed consent is pinned to the exact revision shown to the patient.
 *
 * `allowedCapabilities` maps to the FE `TemplatePolicy.allowedCapabilities`
 * (`['*']` = any); `preferredCapability` to `TemplatePolicy.preferredCapability`.
 */
import type { Capability } from '@/consent/signature';

export type ConsentTemplateStatus = 'draft' | 'published' | 'retired';

/**
 * Authoring mode for a consent template.
 *   - `html`     — bilingual HTML body rendered → PDF server-side (the original model).
 *   - `document` — the clinic uploads its own PDF; the admin drops field boxes onto it
 *                  (the `fieldMap` below) and the server fills + stamps + flattens it.
 *
 * The BE `ConsentTemplate` carries the matching `TemplateMode`; the pipeline branches on it
 * (`html` → `IConsentPdfRenderer`, `document` → `IConsentDocumentFiller`).
 */
export type ConsentTemplateMode = 'html' | 'document';

/**
 * Field kind on the coordinate map. `text` binds to a data path; the rest are image/ink boxes.
 * `signature` is a GENERIC signer field (PROD-1051) — the admin places one box and, at fill time,
 * whoever is present (adult patient OR a minor's guardian) signs it and picks their role. The
 * patient-/guardian-specific types remain for older templates + provider/clinic marks.
 */
export type FieldType =
  | 'text'
  | 'provider-signature'
  | 'clinic-stamp'
  | 'patient-signature'
  | 'guardian-signature'
  | 'signature';

/**
 * One placed box on the uploaded PDF — THE single contract both renderers agree on
 * (FE PDF.js overlay ↔ BE PDFsharp fill). All geometry is FRACTIONAL (0..1) relative to
 * each page's rendered box, so it survives DPI / zoom / page-size differences between the
 * two renderers. The BE serializes the array to `fieldMapJson`; the FE works with the
 * parsed array (see `fieldMap` below).
 */
export interface FieldMapEntry {
  /**
   * For `type: 'text'` — a data path bound at fill-time (e.g. `patient.name`,
   * `patient.emiratesId`, `guardian.name`). For the image/ink types — a stable local id.
   */
  key: string;
  type: FieldType;
  /** 0-based page index. */
  page: number;
  /** Fractional box (0..1 of the page box). */
  xPct: number;
  yPct: number;
  wPct: number;
  hPct: number;
  /** Text height as a fraction of page height (text type only). */
  fontPct?: number;
  /**
   * Static label text (PROD-1051): when set, this box is a fixed label the admin typed in the
   * builder — baked verbatim into every form (no data binding, no fill-time input). Mirrors
   * `ConsentFieldMapEntry.text`.
   */
  text?: string | null;
}

/**
 * The consent type a template stamps on every consent signed against it (PROD-2683).
 * Mirrors Scrips.Patient `ConsentConstants.AmbientRecordingType`; the server allow-lists it.
 * `''` = a general template (the default). On the wire, `''` clears the type and an omitted
 * field leaves it unchanged, so the editors always send one of these two values.
 */
export const AMBIENT_RECORDING_CONSENT_TYPE = 'ambient-recording';
export type ConsentTemplateType = '' | typeof AMBIENT_RECORDING_CONSENT_TYPE;

/** Options for the "Consent type" select in both template editors. */
export const CONSENT_TEMPLATE_TYPE_OPTIONS: { value: ConsentTemplateType; label: string }[] = [
  { value: '', label: 'General (default)' },
  { value: AMBIENT_RECORDING_CONSENT_TYPE, label: 'Ambient recording' },
];

/** Normalise the server's value (null / absent / unknown) to a select value. */
export function toConsentTemplateType(value: string | null | undefined): ConsentTemplateType {
  return value === AMBIENT_RECORDING_CONSENT_TYPE ? AMBIENT_RECORDING_CONSENT_TYPE : '';
}

/** POST body — mirrors `ConsentTemplateRequest`. id null = create, set = update. */
export interface ConsentTemplateBody {
  id?: string | null;
  name: string;
  code: string;
  /** Defaults to `html` server-side when omitted (back-compat). */
  templateMode?: ConsentTemplateMode;
  /** Optional for `document` mode — the uploaded PDF has no HTML body. */
  bodyHtmlEn?: string;
  bodyHtmlAr?: string;
  /** `document` mode — signed Azure-blob URL of the uploaded blank PDF. */
  uploadedPdfUrl?: string | null;
  /** `document` mode — the placed field boxes. Serialized to `fieldMapJson` on the wire. */
  fieldMap?: FieldMapEntry[] | null;
  /** `['*']` = any capability. */
  allowedCapabilities: (Capability | '*')[];
  preferredCapability?: Capability | 'best-available' | null;
  /** JSON schema for merge variables. */
  variableSchema?: string | null;
  /** PROD-2683: `''` = general, `'ambient-recording'`; omitted = leave the stored value alone. */
  consentType?: ConsentTemplateType;
  actorId?: string | null;
}

/** Read shape — mirrors `ConsentTemplateResponse`. */
export interface ConsentTemplate {
  id: string;
  name: string;
  code: string;
  /** `html` for legacy templates (the field may be absent on older rows). */
  templateMode?: ConsentTemplateMode;
  bodyHtmlEn?: string;
  bodyHtmlAr?: string;
  uploadedPdfUrl?: string | null;
  fieldMap?: FieldMapEntry[] | null;
  version: number;
  versionHash: string;
  status: ConsentTemplateStatus | string;
  allowedCapabilities: (Capability | '*')[];
  preferredCapability?: Capability | 'best-available' | null;
  variableSchema?: string | null;
  /** PROD-2683: `'ambient-recording'`, or null / absent for a general template. */
  consentType?: string | null;
  createdDate?: string | null;
  updatedDate?: string | null;
}

/** True when a template uses the uploaded-PDF / coordinate-map model (vs the HTML body). */
export function isDocumentTemplate(t: {
  templateMode?: ConsentTemplateMode;
  uploadedPdfUrl?: string | null;
}): boolean {
  return t.templateMode === 'document' || !!t.uploadedPdfUrl;
}
